import { motion, useMotionValue, useInView, animate } from "motion/react";
import { useRef, useEffect, useState } from "react";

interface CounterProps {
  number: number;
  duration?: number;
  startValue?: number;
  className?: string;
  style?: React.CSSProperties;
  onComplete?: () => void;
  once?: boolean;
  fontStyle?: React.CSSProperties;
}

interface DigitStripProps {
  value: number;
  prevValue: number;
  duration: number;
  position: number;
  style?: React.CSSProperties;
}

const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

// Helper to format number with commas
const formatNumberWithCommas = (value: number): string => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const DigitStrip: React.FC<DigitStripProps> = ({
  value,
  prevValue,
  duration,
  position,
  style,
}) => {
  // For comma, just render it statically
  if (value === -1) {
    return (
      <div style={{ ...style, width: "0.3em", textAlign: "center" }}>,</div>
    );
  }

  // Determine direction: up (increment) or down (decrement)
  let direction = 0;
  if (prevValue !== undefined && prevValue !== -1) {
    if (value === 0 && prevValue === 9)
      direction = 1; // wrap up
    else if (value === 9 && prevValue === 0)
      direction = -1; // wrap down
    else if (value > prevValue) direction = 1;
    else if (value < prevValue) direction = -1;
  }

  // Each digit is 1em tall, so translate by -value * 1em
  const getYPosition = (currentValue: number) => {
    return `-${currentValue}em`;
  };

  return (
    <div
      style={{
        position: "relative",
        height: "1em",
        width: "0.6em",
        overflow: "hidden",
        ...style,
      }}
    >
      <motion.div
        style={{
          position: "absolute",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
        initial={false}
        animate={{ y: getYPosition(value) }}
        transition={{
          duration: position === 0 ? duration * 0.8 : duration * 0.4,
          ease: direction === 1 ? "circOut" : "circIn",
        }}
      >
        {DIGITS.map((digit) => (
          <div
            key={digit}
            style={{
              height: "1em",
              width: "100%",
              lineHeight: "1em",
              textAlign: "center",
            }}
          >
            {digit}
          </div>
        ))}
      </motion.div>
    </div>
  );
};

export default function Counter({
  number,
  duration = 5,
  startValue = 0,
  className,
  style,
  onComplete,
  once = true,
  fontStyle,
}: CounterProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once });
  const count = useMotionValue(startValue);

  // State to trigger re-render on every animation frame
  const [currentValue, setCurrentValue] = useState(startValue);

  // Track previous value for digit animation direction
  const prevValueRef = useRef(startValue);

  useEffect(() => {
    if (isInView) {
      const controls = animate(count, number, {
        duration,
        ease: "linear",
        onComplete,
      });
      return () => controls.stop();
    }
  }, [isInView, number, duration, count, onComplete]);

  // Subscribe to count and update state on every frame
  useEffect(() => {
    const unsubscribe = count.on("change", (v) => {
      setCurrentValue(Math.round(v));
    });
    return unsubscribe;
  }, [count]);

  const formattedValue = formatNumberWithCommas(currentValue);
  const prevFormattedValue = formatNumberWithCommas(prevValueRef.current);

  // Update prevValueRef after render
  useEffect(() => {
    prevValueRef.current = currentValue;
  }, [currentValue]);

  const digits = formattedValue.split("");
  const prevDigits = prevFormattedValue.split("");

  // Pad prevDigits to match digits length
  while (prevDigits.length < digits.length) prevDigits.unshift("0");

  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        display: "flex",
        alignItems: "center",
        height: "1em",
        ...fontStyle,
      }}
      aria-live="polite"
      aria-label={`Current count: ${formattedValue}`}
    >
      {digits.map((char, i) => (
        <DigitStrip
          key={i}
          value={char === "," ? -1 : parseInt(char)}
          prevValue={prevDigits[i] === "," ? -1 : parseInt(prevDigits[i])}
          duration={duration}
          position={digits.length - i - 1}
          style={{ fontFamily: fontStyle?.fontFamily }}
        />
      ))}
    </div>
  );
}
